import { type FC, memo } from "react";
import { TableBody, TableCell, TableRow } from "../../common/components/Table/TableElements";
import { type ABMonitorRowItem } from "../models";
import ProductDesignation from "../../ProductDetails/components/ProductDesignation";
import ProductMMD from "../../feature/Product/elements/ProductMMD";
import { Stacker } from "~/common/components/Stacker/Stacker";
import Chip from "../../common/components/Chip/Chip";
import { DateFormatType, formatDate } from "../../utils/DateFormatter";
import { getPackagingLabelAtLineItem } from "../../Cart/cartUtils";
import { AliasesParameter, SchaefflerIdParameter } from "../../common/components/Product/ProductParams";
import { fakeDescriptorFromProduct } from "../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { NewVersionMessage } from "../../common/components/Messages/NewVersionMessage";
import { Translation } from "../../common/Translation";
import { Link, LinkType } from "../../common/components/Link";
import { getOrderDetailsUrl } from "../../utils/url/urlUtils";
import { obtainPriceString, obtainUnitLabel, type PriceProps, PriceType } from "../../common/components/Price/v1/Price";
import { nonVolatileText } from "../../utils/PriceUtils";

import classes from "./ABMonitorResultsTable.module.less";
import { isMultiAccountOrderSearchEnabled } from "../../utils/features";
import { useTranslation } from "react-i18next";
import { useProductDetailsUrl } from "~/common/components/PackageRestrictionContext/useProductDetailsUrl";
import { hasSuccessor } from "~/utils/productUtils";
import { translateWarehouseName } from "~/utils/language/translationUtils";
import dayjs from "dayjs";
import classNames from "classnames";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { userIsDistributor, userIsPurchaser } from "../../utils/roles";
import Tag from "../../common/components/Tag/Tag";

interface Props {
  item: ABMonitorRowItem;
}

const ABMonitorResultsTableRow: FC<Props> = ({ item }) => {
  const { t } = useTranslation();

  const quantityVisible = item.product.quantityVisible;
  const fakeDescriptor = fakeDescriptorFromProduct(item.product);

  const productDetailsUrl = useProductDetailsUrl(item.product);
  let designation = (
    <ProductDesignation
      designation={item.product.designation}
      size={"label-large"}
      isPreferred={item.product.preferred}
      noCssColor={!!productDetailsUrl}
    />
  );
  if (productDetailsUrl) {
    designation = (
      <Link variant={LinkType.SECONDARY} href={productDetailsUrl} target="_blank">
        {designation}
      </Link>
    );
  }

  const priceProps: PriceProps = {
    price: item.netPrice,
    unit: item.netPrice.unit,
    isSubscription: item.netPrice.isSubscription,
    variant: PriceType.ProductNetPrice,
  };

  const isDistributorOrPurchaser = userIsDistributor() || userIsPurchaser();
  const currentDeliveryDate = dayjs(item.curDeliveryDate);
  const previousDeliveryDate = dayjs(item.prevDeliveryDate);
  const isSameDeliveryDate = currentDeliveryDate.isSame(previousDeliveryDate);
  const isPositiveDeliveryDate = currentDeliveryDate.isBefore(previousDeliveryDate);

  const renderCurrentDeliveryDate = () => {
    if (item.curDeliveryDate && item.prevDeliveryDate && isDistributorOrPurchaser && !isSameDeliveryDate) {
      return (
        <Tag
          className={classes.curDeliveryDate}
          variant={isPositiveDeliveryDate ? "success" : "warn"}
          icon={
            isPositiveDeliveryDate ? (
              <CheckCircleOutlinedIcon className={classes.icon} fontSize="small" />
            ) : (
              <ErrorOutlineIcon className={classNames(classes.icon, classes.yellow)} fontSize="small" />
            )
          }
        >
          {formatDate(item.curDeliveryDate, DateFormatType.DATE)}
        </Tag>
      );
    } else {
      return (
        <span className={classes.confirmedDeliveryDate}>
          {item.curDeliveryDate ? formatDate(item.curDeliveryDate, DateFormatType.DATE) : "-"}
        </span>
      );
    }
  };

  return (
    <TableBody>
      <TableRow>
        <TableCell className={classes.cellInSortableColumn}>
          <p className={"m-0"}>
            <Link href={getOrderDetailsUrl(item.schaefflerOrderNo)} target="_blank">
              {item.schaefflerOrderNo}
            </Link>
          </p>
          <p className={"mb-0 mt-1 f-label-color"}>{item.orderLineNo}</p>
        </TableCell>

        <TableCell className={classes.cellInSortableColumn}>
          <p className={"m-0"}>{item.customerOrderNo}</p>
        </TableCell>

        <TableCell className={classes.cellInSortableColumn}>
          {item.isNew && <Chip type="blue">{t("page.abmonitor.badge.new")}</Chip>}
          {item.isUpdated && <Chip type="grey">{t("page.abmonitor.badge.update")}</Chip>}
          {!item.isNew && !item.isUpdated && " - "}
        </TableCell>

        {isMultiAccountOrderSearchEnabled() && (
          <TableCell className={classes.cellInSortableColumn}>
            <div>
              {item.account?.shortCode}, {item.account?.name}
            </div>
            <div className={"mt-1 f-label-color"}>
              {item.account?.endCustomerCode}, {item.account?.endCustomerName}
            </div>
          </TableCell>
        )}

        <TableCell className={classes.cellInSortableColumn}>
          <Stacker gap={8}>
            {designation}
            {item.product.marketDesignation && <ProductMMD marketDesignation={item.product.marketDesignation} />}
          </Stacker>
          {item.product.shortDescription && (
            <div className="mt-2">
              <Translation {...item.product.shortDescription} />
            </div>
          )}
          <SchaefflerIdParameter className={"mt-2"} productDescriptor={fakeDescriptor} />
        </TableCell>

        <TableCell>
          <p className={"my-0"}>
            {getPackagingLabelAtLineItem({
              t,
              productDescriptor: fakeDescriptor,
              quantity: item.totalQuantity,
              packCode: item.packCode,
              packSize: item.packSize,
            })}
            {quantityVisible && (
              <p className={"mb-0 mt-1 f-label-color"}>{translateWarehouseName(t, item.primaryWarehouseName)}</p>
            )}
          </p>
          <AliasesParameter className={"mt-15"} product={item.product} mode={"all"} />
        </TableCell>

        <TableCell className={classes.cellInSortableColumn}>
          <p className={"m-0"}>
            {t("page.abmonitor.confirmedQuantity", { current: item.curQuantity, total: item.totalQuantity })}
          </p>
          <p className={"mb-0 mt-1 f-label-color"}>{item.prevQuantity}</p>
        </TableCell>

        <TableCell>
          <p className={"m-0"}>{obtainPriceString(priceProps, t, undefined)}</p>
          <p className={"mb-0 mt-1 f-label-color"}>{obtainUnitLabel(priceProps, t)}</p>
        </TableCell>

        <TableCell>
          <p className={"m-0 f-bold"}>{nonVolatileText(item.totalPrice)}</p>
        </TableCell>

        <TableCell className={classes.cellInSortableColumn}>
          {renderCurrentDeliveryDate()}
          <p className={classNames(classes.confirmedDeliveryDate, "mb-0 mt-1 f-label-color")}>
            {item.prevDeliveryDate ? formatDate(item.prevDeliveryDate, DateFormatType.DATE) : "-"}
          </p>
        </TableCell>

        <TableCell className={classes.cellInSortableColumn}>
          {item.requestedDeliveryDate ? formatDate(item.requestedDeliveryDate, DateFormatType.DATE) : "-"}
        </TableCell>
      </TableRow>

      {hasSuccessor(item.product) && (
        <TableRow className={classes.extraRow}>
          <TableCell colSpan={isMultiAccountOrderSearchEnabled() ? 4 : 3} />

          <TableCell colSpan={3}>
            <NewVersionMessage baseProduct={item.product} />
          </TableCell>

          <TableCell colSpan={4} />
        </TableRow>
      )}
    </TableBody>
  );
};

export default memo(ABMonitorResultsTableRow);
